import type {
  RestaurantSemData,
  SemData,
  SemSummary,
  Campaign,
  CampaignMetrics,
  AdGroup,
  AdGroupMetrics,
  Ad,
  AdMetrics,
  RsaAsset,
  RsaPerformance,
  RsaAssetSummary,
  KeywordData,
  KeywordMetrics,
  AiInsight,
  PageAudit,
  CompetitorAudit,
  ConversionAction,
} from '@/types/sem';

// ---------------------------------------------------------------------------
// Supabase row types
// ---------------------------------------------------------------------------

export interface DailySnapshotRow {
  restaurant_id: string;
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  conversion_value: number;
  ctr: number;
  cpc: number;
  cpa: number;
  roas: number;
  impression_share: number;
  quality_score_avg: number;
}

export interface CampaignSnapshotRow {
  restaurant_id: string;
  campaign_id: string;
  date: string;
  name: string;
  status: string;
  type: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  conversion_value: number;
  ctr: number;
  cpc: number;
  cpa: number;
  roas: number;
  impression_share: number;
}

export interface AdGroupSnapshotRow {
  restaurant_id: string;
  campaign_id: string;
  ad_group_id: string;
  date: string;
  name: string;
  status: string;
  quality_score: number | null;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  conversion_value: number;
  ctr: number;
  cpc: number;
  cpa: number;
  roas: number;
}

export interface RsaAssetRow {
  restaurant_id: string;
  ad_group_id: string;
  ad_id: string;
  asset_type: string;
  text: string;
  date: string;
  pinned_position: number | null;
  performance_rating: string;
  impressions: number;
}

export interface AdSnapshotRow {
  restaurant_id: string;
  campaign_id: string;
  ad_group_id: string;
  ad_id: string;
  date: string;
  final_url: string | null;
  ad_type: string;
  status: string;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
}

export interface KeywordSnapshotRow {
  restaurant_id: string;
  campaign_id: string;
  campaign_name: string;
  ad_group_id: string;
  ad_group_name: string;
  keyword_text: string;
  match_type: string;
  status: string;
  date: string;
  impressions: number;
  clicks: number;
  conversions: number;
  spend: number;
  ctr: number;
  cpc: number;
  cpa: number;
  quality_score: number | null;
  impression_share: number;
}

export interface RestaurantRow {
  id: string;
  name: string;
  google_ads_customer_id: string;
  location: string;
  monthly_budget: number;
}

export interface SyncLogRow {
  id: string;
  started_at: string;
  completed_at: string | null;
  status: string;
  accounts_synced: number;
  accounts_total: number;
  error_message: string | null;
}

export interface AiInsightRow {
  id: string;
  restaurant_id: string;
  campaign_id: string;
  ad_group_id: string;
  tier: string;
  cvr_at_trigger: number;
  clicks: number;
  conversions: number;
  headline_suggestions: any;
  optimization_notes: string | null;
  estimated_impact: string | null;
  landing_page_issues: any;
  rewrite_recommendations: any;
  priority_action: string | null;
  model_used: string;
  prompt_version: string;
  generated_at: string;
  sync_date: string;
}

export interface PageAuditRow {
  id: string;
  restaurant_id: string;
  page_url: string;
  screenshot_path: string | null;
  viewport_width: number;
  viewport_height: number | null;
  fold_y: number;
  element_map: any;
  annotations: any;
  overall_score: number | null;
  issue_count: number;
  total_revenue_at_risk: number;
  model_used: string;
  analyzed_at: string;
}

export interface CompetitorAuditRow {
  id: string;
  restaurant_id: string;
  keyword: string;
  competitor_domain: string;
  competitor_name: string | null;
  competitor_ad_copy: any;
  competitor_page_url: string | null;
  competitor_screenshot_path: string | null;
  your_score: number | null;
  competitor_score: number | null;
  element_comparison: any;
  ai_analysis: string | null;
  total_gap_cost: number;
  model_used: string;
  analyzed_at: string;
}

export interface ConversionActionRow {
  restaurant_id: string;
  campaign_id: string;
  campaign_name: string | null;
  ad_group_id: string;
  ad_group_name: string | null;
  conversion_action_name: string;
  conversion_action_category: string | null;
  date: string;
  conversions: number;
  conversion_value: number;
  view_through_conversions: number;
}

// ---------------------------------------------------------------------------
// Aggregation: sum daily snapshots over a date range
// ---------------------------------------------------------------------------

export function aggregateSnapshots(rows: DailySnapshotRow[]): SemSummary {
  if (rows.length === 0) {
    return { spend: 0, impressions: 0, clicks: 0, conversions: 0, conversionValue: 0, ctr: 0, cpc: 0, cpa: 0, roas: 0, impressionShare: 0, qualityScoreAvg: 0 };
  }

  // Cap per-conversion value to filter out inflated Google Ads placeholders
  // (e.g., "Form Submission" actions assigned $15K default values)
  const MAX_VALUE_PER_CONV = 500;

  const totals = rows.reduce(
    (acc, r) => {
      const cappedConvValue = r.conversions > 0
        ? Math.min(r.conversion_value, r.conversions * MAX_VALUE_PER_CONV)
        : r.conversion_value;
      return {
        spend: acc.spend + r.spend,
        impressions: acc.impressions + r.impressions,
        clicks: acc.clicks + r.clicks,
        conversions: acc.conversions + r.conversions,
        conversionValue: acc.conversionValue + cappedConvValue,
        impressionShare: acc.impressionShare + r.impression_share,
        qualityScoreSum: acc.qualityScoreSum + r.quality_score_avg,
      };
    },
    { spend: 0, impressions: 0, clicks: 0, conversions: 0, conversionValue: 0, impressionShare: 0, qualityScoreSum: 0 }
  );

  return {
    ...totals,
    ctr: totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0,
    cpc: totals.clicks > 0 ? totals.spend / totals.clicks : 0,
    cpa: totals.conversions > 0 ? totals.spend / totals.conversions : 0,
    roas: totals.spend > 0 ? totals.conversionValue / totals.spend : 0,
    impressionShare: totals.impressionShare / rows.length,
    qualityScoreAvg: totals.qualityScoreSum / rows.length,
  };
}

// ---------------------------------------------------------------------------
// Build RestaurantSemData from Supabase rows (for backward compat)
// ---------------------------------------------------------------------------

export function buildRestaurantSemData(
  restaurant: RestaurantRow,
  snapshots: DailySnapshotRow[],
  campaignRows: CampaignSnapshotRow[],
  adGroupRows: AdGroupSnapshotRow[],
  rsaRows: RsaAssetRow[],
  adRows: AdSnapshotRow[],
  keywordRows: KeywordSnapshotRow[],
  dateRange: { start: string; end: string },
  aiInsightRows?: AiInsightRow[],
  pageAuditRows?: PageAuditRow[],
  competitorAuditRows?: CompetitorAuditRow[],
  conversionActionRows?: ConversionActionRow[],
): RestaurantSemData {
  const summary = aggregateSnapshots(snapshots);

  // Helper: aggregate conversion action rows by action name (raw values, no close-rate adjustments)
  function aggregateConvActions(rows: ConversionActionRow[]): ConversionAction[] {
    const map = new Map<string, { conversions: number; conversionValue: number; viewThrough: number; category: string | null; valuePerEvent: number }>();
    for (const ca of rows) {
      const existing = map.get(ca.conversion_action_name);
      if (existing) {
        existing.conversions += ca.conversions;
        existing.conversionValue += ca.conversion_value;
        existing.viewThrough += ca.view_through_conversions;
      } else {
        // Capture the exact per-event value from Google Ads (value / conversions for a single row)
        const perEvent = ca.conversions > 0 ? ca.conversion_value / ca.conversions : 0;
        map.set(ca.conversion_action_name, {
          conversions: ca.conversions,
          conversionValue: ca.conversion_value,
          viewThrough: ca.view_through_conversions,
          category: ca.conversion_action_category,
          valuePerEvent: perEvent,
        });
      }
    }
    return Array.from(map.entries())
      .map(([actionName, data]) => ({
        actionName,
        category: data.category,
        conversions: data.conversions,
        conversionValue: data.conversionValue,
        valuePerEvent: data.valuePerEvent,
        viewThroughConversions: data.viewThrough,
      }))
      .sort((a, b) => b.conversions - a.conversions);
  }

  // Group campaigns: aggregate across dates
  const campaignMap = new Map<string, CampaignSnapshotRow[]>();
  for (const c of campaignRows) {
    const existing = campaignMap.get(c.campaign_id) || [];
    existing.push(c);
    campaignMap.set(c.campaign_id, existing);
  }

  const MAX_CONV_VALUE = 500; // Same cap as aggregateSnapshots

  const campaigns: Campaign[] = Array.from(campaignMap.entries()).map(([id, rows]) => {
    const latest = rows[rows.length - 1];
    const totals = rows.reduce(
      (acc, r) => {
        const cappedVal = r.conversions > 0
          ? Math.min(r.conversion_value, r.conversions * MAX_CONV_VALUE)
          : r.conversion_value;
        return {
          spend: acc.spend + r.spend,
          impressions: acc.impressions + r.impressions,
          clicks: acc.clicks + r.clicks,
          conversions: acc.conversions + r.conversions,
          conversionValue: acc.conversionValue + cappedVal,
        };
      },
      { spend: 0, impressions: 0, clicks: 0, conversions: 0, conversionValue: 0 }
    );

    const metrics: CampaignMetrics = {
      ...totals,
      ctr: totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0,
      cpc: totals.clicks > 0 ? totals.spend / totals.clicks : 0,
      cpa: totals.conversions > 0 ? totals.spend / totals.conversions : 0,
      roas: totals.spend > 0 ? totals.conversionValue / totals.spend : 0,
      impressionShare: rows.reduce((s, r) => s + r.impression_share, 0) / rows.length,
    };

    // Build ad groups for this campaign
    const campaignAdGroups = adGroupRows.filter(ag => ag.campaign_id === id);
    const agMap = new Map<string, AdGroupSnapshotRow[]>();
    for (const ag of campaignAdGroups) {
      const existing = agMap.get(ag.ad_group_id) || [];
      existing.push(ag);
      agMap.set(ag.ad_group_id, existing);
    }

    const adGroups: AdGroup[] = Array.from(agMap.entries()).map(([agId, agRows]) => {
      const agLatest = agRows[agRows.length - 1];
      const agTotals = agRows.reduce(
        (acc, r) => {
          const cappedVal = r.conversions > 0
            ? Math.min(r.conversion_value, r.conversions * MAX_CONV_VALUE)
            : r.conversion_value;
          return {
            spend: acc.spend + r.spend,
            impressions: acc.impressions + r.impressions,
            clicks: acc.clicks + r.clicks,
            conversions: acc.conversions + r.conversions,
            conversionValue: acc.conversionValue + cappedVal,
          };
        },
        { spend: 0, impressions: 0, clicks: 0, conversions: 0, conversionValue: 0 }
      );
      const agMetrics: AdGroupMetrics = {
        ...agTotals,
        ctr: agTotals.impressions > 0 ? (agTotals.clicks / agTotals.impressions) * 100 : 0,
        cpc: agTotals.clicks > 0 ? agTotals.spend / agTotals.clicks : 0,
        cpa: agTotals.conversions > 0 ? agTotals.spend / agTotals.conversions : 0,
        roas: agTotals.spend > 0 ? agTotals.conversionValue / agTotals.spend : 0,
        qualityScore: agLatest.quality_score || 0,
      };
      // Build ads for this ad group
      const agAdRows = adRows.filter(a => a.ad_group_id === agId);
      const adMap = new Map<string, AdSnapshotRow[]>();
      for (const a of agAdRows) {
        const existing = adMap.get(a.ad_id) || [];
        existing.push(a);
        adMap.set(a.ad_id, existing);
      }

      const ads: Ad[] = Array.from(adMap.entries()).map(([adId, rows]) => {
        const latest = rows[rows.length - 1];
        const adTotals = rows.reduce(
          (acc, r) => ({
            impressions: acc.impressions + r.impressions,
            clicks: acc.clicks + r.clicks,
            conversions: acc.conversions + r.conversions,
          }),
          { impressions: 0, clicks: 0, conversions: 0 }
        );
        const adMetrics: AdMetrics = {
          ...adTotals,
          ctr: adTotals.impressions > 0 ? (adTotals.clicks / adTotals.impressions) * 100 : 0,
        };

        // Attach RSA headlines/descriptions for this ad
        const adRsaRows = rsaRows.filter(r => r.ad_id === adId);
        const headlines: RsaAsset[] = adRsaRows
          .filter(r => r.asset_type === 'headline')
          .map(r => ({
            text: r.text,
            pinnedPosition: r.pinned_position as RsaAsset['pinnedPosition'],
            performanceRating: r.performance_rating as RsaAsset['performanceRating'],
            impressions: r.impressions,
          }));
        const descriptions: RsaAsset[] = adRsaRows
          .filter(r => r.asset_type === 'description')
          .map(r => ({
            text: r.text,
            pinnedPosition: r.pinned_position as RsaAsset['pinnedPosition'],
            performanceRating: r.performance_rating as RsaAsset['performanceRating'],
            impressions: r.impressions,
          }));

        return {
          id: adId,
          type: latest.ad_type as Ad['type'],
          status: latest.status as Ad['status'],
          finalUrl: latest.final_url || '',
          metrics: adMetrics,
          headlines: headlines.length > 0 ? headlines : undefined,
          descriptions: descriptions.length > 0 ? descriptions : undefined,
        };
      });

      // Aggregate conversion actions for this ad group
      const agConvActions = aggregateConvActions(
        (conversionActionRows || []).filter(ca => ca.campaign_id === id && ca.ad_group_id === agId)
      );

      return {
        id: agId,
        name: agLatest.name,
        status: agLatest.status as 'enabled' | 'paused' | 'removed',
        metrics: agMetrics,
        ads,
        conversionActions: agConvActions.length > 0 ? agConvActions : undefined,
      };
    });

    // Aggregate conversion actions for this campaign
    const campConvActions = aggregateConvActions(
      (conversionActionRows || []).filter(ca => ca.campaign_id === id)
    );

    return {
      id,
      name: latest.name,
      status: latest.status as 'enabled' | 'paused' | 'removed',
      type: latest.type as Campaign['type'],
      metrics,
      adGroups,
      conversionActions: campConvActions.length > 0 ? campConvActions : undefined,
    };
  });

  // RSA performance aggregation
  const headlineMap = new Map<string, RsaAssetSummary>();
  const descriptionMap = new Map<string, RsaAssetSummary>();
  let totalAds = new Set<string>();
  for (const r of rsaRows) {
    totalAds.add(r.ad_id);
    const map = r.asset_type === 'headline' ? headlineMap : descriptionMap;
    const existing = map.get(r.text);
    if (existing) {
      existing.impressions += r.impressions;
      existing.adsUsedIn += 1;
    } else {
      map.set(r.text, {
        text: r.text,
        rating: r.performance_rating as RsaAssetSummary['rating'],
        impressions: r.impressions,
        adsUsedIn: 1,
      });
    }
  }

  const rsaPerformance: RsaPerformance = {
    totalAds: totalAds.size,
    headlines: Array.from(headlineMap.values()).sort((a, b) => b.impressions - a.impressions),
    descriptions: Array.from(descriptionMap.values()).sort((a, b) => b.impressions - a.impressions),
  };

  // Build keyword data
  const kwMap = new Map<string, KeywordSnapshotRow[]>();
  for (const kw of keywordRows) {
    const key = `${kw.ad_group_id}|${kw.keyword_text}|${kw.match_type}`;
    const existing = kwMap.get(key) || [];
    existing.push(kw);
    kwMap.set(key, existing);
  }

  const keywords: KeywordData[] = Array.from(kwMap.values()).map((rows) => {
    const latest = rows[rows.length - 1];
    const kwTotals = rows.reduce(
      (acc, r) => ({
        impressions: acc.impressions + r.impressions,
        clicks: acc.clicks + r.clicks,
        conversions: acc.conversions + r.conversions,
        spend: acc.spend + r.spend,
      }),
      { impressions: 0, clicks: 0, conversions: 0, spend: 0 }
    );
    const kwMetrics: KeywordMetrics = {
      ...kwTotals,
      ctr: kwTotals.impressions > 0 ? (kwTotals.clicks / kwTotals.impressions) * 100 : 0,
      cpc: kwTotals.clicks > 0 ? kwTotals.spend / kwTotals.clicks : 0,
      cpa: kwTotals.conversions > 0 ? kwTotals.spend / kwTotals.conversions : 0,
      qualityScore: latest.quality_score || 0,
      impressionShare: rows.reduce((s, r) => s + r.impression_share, 0) / rows.length,
    };
    return {
      keyword: latest.keyword_text,
      matchType: latest.match_type as KeywordData['matchType'],
      campaignName: latest.campaign_name,
      adGroupName: latest.ad_group_name,
      status: latest.status as KeywordData['status'],
      metrics: kwMetrics,
    };
  });

  const semData: SemData = {
    meta: {
      accountName: restaurant.name,
      accountId: restaurant.google_ads_customer_id,
      dateRange: dateRange,
      currency: 'USD',
      generatedAt: new Date().toISOString(),
    },
    summary,
    campaigns,
    rsaPerformance,
    keywords,
    recommendations: [],
  };

  // Pick latest page audit (by analyzed_at)
  const latestPageAudit = pageAuditRows && pageAuditRows.length > 0
    ? pageAuditRows.sort((a, b) => b.analyzed_at.localeCompare(a.analyzed_at))[0]
    : undefined;

  // Parse JSON fields that may come as strings from Supabase
  function parseJsonField(val: any): any {
    if (typeof val === 'string') {
      try { return JSON.parse(val); } catch { return val; }
    }
    return val;
  }

  // Normalize competitor audit rows — parse JSON string fields
  const normalizedCompetitorAudits = (competitorAuditRows || []).map(ca => ({
    ...ca,
    element_comparison: parseJsonField(ca.element_comparison) || [],
    competitor_ad_copy: parseJsonField(ca.competitor_ad_copy) || { headlines: [], descriptions: [] },
  }));

  // Normalize AI insight rows — parse JSON string fields
  const normalizedInsights = (aiInsightRows || []).map(ins => ({
    ...ins,
    headline_suggestions: parseJsonField(ins.headline_suggestions) || [],
    landing_page_issues: parseJsonField(ins.landing_page_issues) || null,
    rewrite_recommendations: parseJsonField(ins.rewrite_recommendations) || null,
  }));

  // Pick competitor audit with highest total_gap_cost (most impactful comparison)
  const topCompetitorAudit = normalizedCompetitorAudits.length > 0
    ? normalizedCompetitorAudits.sort((a, b) => Math.abs(b.total_gap_cost) - Math.abs(a.total_gap_cost))[0]
    : undefined;

  // Aggregate conversion actions at restaurant level using shared helper
  const conversionActions = aggregateConvActions(conversionActionRows || []);

  return {
    id: restaurant.id,
    name: restaurant.name,
    data: semData,
    aiInsights: normalizedInsights as AiInsight[],
    pageAudit: latestPageAudit as PageAudit | undefined,
    competitorAudit: topCompetitorAudit as CompetitorAudit | undefined,
    conversionActions: conversionActions.length > 0 ? conversionActions : undefined,
  };
}

// ---------------------------------------------------------------------------
// Period comparison utility
// ---------------------------------------------------------------------------

export interface PeriodComparison {
  value: number;
  previousValue: number;
  percentChange: number;
  direction: 'up' | 'down' | 'flat';
}

/**
 * Compare a metric across two equal-length time windows.
 * For additive metrics (spend, clicks, conversions): sums rows.
 * For ratio metrics (roas, cpa, ctr, quality_score_avg): recomputes from components.
 */
export function comparePeriods(
  currentRows: DailySnapshotRow[],
  previousRows: DailySnapshotRow[],
  metric: keyof DailySnapshotRow,
): PeriodComparison {
  const RATIO_METRICS = ['roas', 'cpa', 'ctr', 'cpc', 'quality_score_avg'];

  let current: number;
  let previous: number;

  if (RATIO_METRICS.includes(metric as string)) {
    // Recompute ratio from components instead of summing
    const sumField = (rows: DailySnapshotRow[], field: keyof DailySnapshotRow) =>
      rows.reduce((s, r) => s + (r[field] as number), 0);

    if (metric === 'roas') {
      const curSpend = sumField(currentRows, 'spend');
      const curValue = sumField(currentRows, 'conversion_value');
      const prevSpend = sumField(previousRows, 'spend');
      const prevValue = sumField(previousRows, 'conversion_value');
      current = curSpend > 0 ? curValue / curSpend : 0;
      previous = prevSpend > 0 ? prevValue / prevSpend : 0;
    } else if (metric === 'cpa') {
      const curSpend = sumField(currentRows, 'spend');
      const curConv = sumField(currentRows, 'conversions');
      const prevSpend = sumField(previousRows, 'spend');
      const prevConv = sumField(previousRows, 'conversions');
      current = curConv > 0 ? curSpend / curConv : 0;
      previous = prevConv > 0 ? prevSpend / prevConv : 0;
    } else if (metric === 'ctr') {
      const curClicks = sumField(currentRows, 'clicks');
      const curImpr = sumField(currentRows, 'impressions');
      const prevClicks = sumField(previousRows, 'clicks');
      const prevImpr = sumField(previousRows, 'impressions');
      current = curImpr > 0 ? (curClicks / curImpr) * 100 : 0;
      previous = prevImpr > 0 ? (prevClicks / prevImpr) * 100 : 0;
    } else if (metric === 'cpc') {
      const curSpend = sumField(currentRows, 'spend');
      const curClicks = sumField(currentRows, 'clicks');
      const prevSpend = sumField(previousRows, 'spend');
      const prevClicks = sumField(previousRows, 'clicks');
      current = curClicks > 0 ? curSpend / curClicks : 0;
      previous = prevClicks > 0 ? prevSpend / prevClicks : 0;
    } else {
      // quality_score_avg: simple average
      current = currentRows.length > 0 ? sumField(currentRows, metric) / currentRows.length : 0;
      previous = previousRows.length > 0 ? sumField(previousRows, metric) / previousRows.length : 0;
    }
  } else {
    // Additive metrics: sum
    current = currentRows.reduce((sum, r) => sum + (r[metric] as number), 0);
    previous = previousRows.reduce((sum, r) => sum + (r[metric] as number), 0);
  }

  if (previous === 0) {
    return { value: current, previousValue: 0, percentChange: 0, direction: 'flat' };
  }

  const pct = ((current - previous) / previous) * 100;
  return {
    value: current,
    previousValue: previous,
    percentChange: pct,
    direction: Math.abs(pct) < 1 ? 'flat' : pct > 0 ? 'up' : 'down',
  };
}
